// @ts-nocheck
import * as React from "react";
import { Logo, siteName, brandParts, Ic, PasswordInput, Sidebar, Topbar, LineChart, BarChart, Kpi, AppFooter, apiErrorMessage } from "./common";

// Auth — real login (1280x832 design viewport).

const SAVED_EMAIL_KEY = "care_saved_email";

function Auth() {
  // "아이디 저장": 마지막으로 저장한 이메일을 localStorage에서 읽어 프리필하고,
  // 저장된 값이 있으면 체크박스를 켠 상태로 시작한다(비밀번호는 저장하지 않음).
  const savedEmail = (() => { try { return localStorage.getItem(SAVED_EMAIL_KEY) || ""; } catch { return ""; } })();
  const [email, setEmail] = React.useState(savedEmail);
  const [password, setPassword] = React.useState("");
  const [saveId, setSaveId] = React.useState(!!savedEmail);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [demoRole, setDemoRole] = React.useState(null); // 진행 중인 데모 역할 키(스피너용)
  const [demoRegions, setDemoRegions] = React.useState([]); // [{id,name,roles}] — 등록된 전체 권역
  const [sysAdmin, setSysAdmin] = React.useState(false);    // system_admin 데모 가용 여부(전역)
  const [regionId, setRegionId] = React.useState(null);     // 선택된 권역 id

  // 데모 체험: 계정 유형을 고르면 서버가 (선택 권역, 역할)에 맞는 대표 계정으로
  // 세션을 발급한다. 비밀번호는 프런트에 두지 않는다(서버가 DB에서 선택).
  const demoRoles = [
    { key: "system_admin", label: "시스템 관리자", desc: "전국 플랫폼 대시보드",  icon: Ic.dashboard },
    { key: "region_admin", label: "권역 관리자",   desc: "권역 운영·승인·매칭",   icon: Ic.region },
    { key: "manager",      label: "케어매니저",     desc: "모바일 방문·케이스",     icon: Ic.manager },
    { key: "agency",       label: "유관기관",       desc: "기관 대시보드·지도",     icon: Ic.org },
    { key: "guardian",     label: "보호자",         desc: "어르신 상태·알림",       icon: Ic.heart },
    { key: "elderly",      label: "어르신",         desc: "복약·일정 홈",           icon: Ic.user },
  ];

  // 등록된 전체 권역 + 권역별 가용 역할 로드. 기본 선택은 계정이 가장 많은 권역.
  React.useEffect(() => {
    fetch("/api/v1/sessions/demo_regions", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : { regions: [] })
      .then(j => {
        const regs = j.regions || [];
        setDemoRegions(regs);
        setSysAdmin(!!j.system_admin);
        if (regs.length) {
          const best = regs.reduce((a, b) => (b.roles.length > a.roles.length ? b : a), regs[0]);
          setRegionId(best.id);
        }
      })
      .catch(() => {});
  }, []);

  const activeRegion = demoRegions.find(r => r.id === regionId) || null;
  const regionRoleKeys = activeRegion ? activeRegion.roles : [];
  // system_admin(전역) 카드는 가용하면 항상 맨 앞에, 이어서 선택 권역의 역할.
  const availableRoles = (sysAdmin ? ["system_admin"] : []).concat(regionRoleKeys);
  const visibleRoles = demoRoles.filter(d => availableRoles.includes(d.key));

  const demoLogin = async (role) => {
    if (loading || demoRole) return;
    setErr(null);
    setDemoRole(role);
    try {
      const body = role === "system_admin" ? { role } : { role, region: regionId };
      const r = await fetch("/api/v1/sessions/demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin",
      });
      if (r.ok) {
        const u = await r.json();
        window.location.href = u.must_change_password ? "/password-change" : (u.default_path || "/");
      } else {
        const j = await r.json().catch(() => ({}));
        setErr(apiErrorMessage(j, r.status));
        setDemoRole(null);
      }
    } catch (e) { setErr(apiErrorMessage(e)); setDemoRole(null); }
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const r = await fetch("/api/v1/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "same-origin",
      });
      if (r.ok) {
        // 로그인 성공 시에만 이메일을 저장/삭제(실패한 오타 이메일은 남기지 않음).
        try {
          if (saveId) localStorage.setItem(SAVED_EMAIL_KEY, email);
          else localStorage.removeItem(SAVED_EMAIL_KEY);
        } catch {}
        const u = await r.json();
        // 비밀번호 만료 시 강제 변경 화면으로. 그 외에는 역할별 기본 화면으로.
        window.location.href = u.must_change_password ? "/password-change" : (u.default_path || "/");
      } else {
        const j = await r.json().catch(() => ({}));
        // 서버가 주는 친화 메시지(계정 잠금/탈퇴 안내 등)를 그대로 살리고,
        // 없으면 코드맵/상태 폴백. 기계용 코드는 노출하지 않는다.
        setErr(apiErrorMessage(j, r.status));
      }
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setLoading(false); }
  };

  const bp = brandParts(siteName());
  const trust = [
    { t: '권역 통합', d: '한 화면 운영' },
    { t: 'AI 매칭', d: '최적 케어 제안' },
    { t: '감사 보존', d: '5년 추적' },
  ];

  return (
    <div className="app" style={{display:'flex', flexDirection:'column', minHeight:'100vh', background:'var(--bg)'}}>
    <div className="auth-grid">
      {/* 좁은 화면에서는 숨겨지는 브랜드 패널 (CSS .auth-brand) */}
      <div className="auth-brand">
        <a href="/" style={{display:'flex', alignItems:'center', gap:10, textDecoration:'none', color:'inherit'}}>
          <div className="brand-mark" style={{width:32, height:32}}>{bp.mark}</div>
          <div>
            <div style={{fontWeight:800, fontSize:15, color:'#fff'}}>{bp.name}</div>
            {bp.sub && <div style={{fontSize:10, color:'#A89A7C', letterSpacing:'0.06em'}}>{bp.sub}</div>}
          </div>
        </a>

        <div style={{marginTop:'auto', marginBottom:'auto', paddingTop:48, paddingBottom:48}}>
          <div style={{fontSize:11, color:'var(--orange-300)', fontWeight:700, letterSpacing:'0.08em', marginBottom:14}}>2026 돌봄통합지원법 대응</div>
          <h1 style={{fontSize:38, fontWeight:800, lineHeight:1.2, letterSpacing:'-0.02em', margin:0, color:'#fff'}}>
            한 권역의 돌봄을<br/>하나의 화면에서.
          </h1>
          <p style={{fontSize:14, lineHeight:1.7, color:'#D4C9B2', marginTop:18, maxWidth:380}}>
            케어매니저·유관기관·보호자·관리자가 같은 케이스를 함께 보고, AI가 가장 적합한 매칭을 제안합니다.
          </p>
        </div>

        <div style={{display:'flex', gap:28, paddingTop:24, borderTop:'1px solid rgba(255,255,255,0.12)'}}>
          {trust.map((x,i)=>(
            <div key={i}>
              <div style={{fontSize:13, fontWeight:800, color:'#fff'}}>{x.t}</div>
              <div style={{fontSize:11, color:'#A89A7C', marginTop:3}}>{x.d}</div>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={submit} className="auth-panel">
        <div className="auth-panel-top">
          {/* 모바일 전용 로고 (브랜드 패널이 숨겨질 때 노출) */}
          <a href="/" className="auth-mobile-logo" style={{alignItems:'center', gap:9, textDecoration:'none', color:'inherit'}}>
            <div className="brand-mark" style={{width:28, height:28}}>{bp.mark}</div>
            <div style={{fontWeight:800, fontSize:14}}>{bp.name}</div>
          </a>
          <div style={{flex:1}}/>
          <div style={{fontSize:12, color:'var(--ink-3)'}}>
            처음이신가요? <a href="/signup" style={{color:'var(--orange-600)', fontWeight:700, marginLeft:6, textDecoration:'none'}}>회원가입 →</a>
          </div>
        </div>

        <div className="auth-form-inner">
          <div style={{marginBottom:24}}>
            <h2 style={{fontSize:28, fontWeight:800, letterSpacing:'-0.02em', margin:0}}>로그인</h2>
            <p style={{color:'var(--ink-3)', fontSize:13, marginTop:8}}>가입하신 이메일과 비밀번호로 로그인하세요.</p>
          </div>

          {err && (
            <div role="alert" style={{display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5, marginBottom:16}}>
              <span style={{width:16, height:16, flex:'0 0 auto', display:'grid', placeItems:'center', fontSize:16}}>{Ic.alert}</span>
              <span>{err}</span>
            </div>
          )}

          <div className="label">이메일</div>
          <input className="input" value={email} onChange={e=>setEmail(e.target.value)} type="email" autoComplete="email" required style={{marginBottom:14}}/>

          <div className="label">비밀번호</div>
          <PasswordInput value={password} onChange={e=>setPassword(e.target.value)}
                         autoComplete="current-password" required/>

          <div style={{display:'flex', gap:8, alignItems:'center', marginTop:18, fontSize:12, color:'var(--ink-3)'}}>
            <label style={{display:'flex', alignItems:'center', gap:6, cursor:'pointer'}}>
              <input type="checkbox" checked={saveId} onChange={e=>setSaveId(e.target.checked)}/> 아이디 저장
            </label>
            <div style={{flex:1}}/>
            <a href="/forgot-password" style={{color:'var(--orange-600)', fontWeight:600, textDecoration:'none'}}>비밀번호 찾기</a>
          </div>

          <button type="submit" disabled={loading || !!demoRole} className="btn btn-primary btn-lg" style={{marginTop:24, width:'100%'}}>
            {loading ? '로그인 중…' : '로그인 →'}
          </button>

          {/* 데모 체험 — 권역 선택 + 계정 유형별 자동 로그인 */}
          <div style={{marginTop:28}}>
            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:14}}>
              <div style={{flex:1, height:1, background:'var(--line)'}}/>
              <span style={{fontSize:11, fontWeight:700, letterSpacing:'0.04em', color:'var(--ink-3)'}}>데모 체험 · 계정 유형 선택</span>
              <div style={{flex:1, height:1, background:'var(--line)'}}/>
            </div>

            {demoRegions.length > 0 && (
              <div style={{marginBottom:12}}>
                <div className="label" style={{display:'flex', alignItems:'center', gap:6}}>
                  <span style={{width:14, height:14, display:'grid', placeItems:'center', fontSize:14, color:'var(--ink-3)'}}>{Ic.region}</span>
                  권역 선택
                </div>
                <select
                  className="input"
                  value={regionId ?? ''}
                  onChange={e=>setRegionId(Number(e.target.value))}
                  disabled={loading || !!demoRole}
                  style={{cursor:'pointer'}}
                >
                  {demoRegions.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name}{r.roles.length === 0 ? ' (계정 없음)' : ` · ${r.roles.length}종`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {activeRegion && regionRoleKeys.length === 0 && (
              <div style={{fontSize:11.5, color:'var(--ink-3)', background:'var(--bg)', border:'1px dashed var(--line)', borderRadius:10, padding:'10px 12px', marginBottom:8, textAlign:'center'}}>
                {activeRegion.name}에는 개별 데모 계정이 없습니다.{sysAdmin ? ' 시스템 관리자로 전체를 둘러볼 수 있어요.' : ''}
              </div>
            )}

            {/* 2열 고정이면 좁은 폰(≤360px)에서 버튼이 카드 밖으로 삐져나온다.
                auto-fit 으로 폭이 모자라면 1열로 접고, 그리드 아이템의 기본
                min-width:auto(=콘텐츠 최소폭) 도 minWidth:0 으로 풀어준다. */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(128px, 1fr))', gap:8}}>
              {visibleRoles.map((d) => {
                const busy = demoRole === d.key;
                return (
                  <button
                    key={d.key} type="button" onClick={() => demoLogin(d.key)}
                    disabled={loading || (!!demoRole && !busy)}
                    style={{
                      display:'flex', alignItems:'center', gap:10, textAlign:'left', minWidth:0,
                      padding:'10px 12px', borderRadius:10, cursor:'pointer',
                      border:'1px solid var(--line)', background:'var(--surface, #fff)',
                      opacity:(loading || (!!demoRole && !busy)) ? 0.55 : 1,
                      transition:'border-color .12s, background .12s',
                    }}
                    onMouseEnter={e=>{ if(!demoRole){ e.currentTarget.style.borderColor='var(--orange-400)'; e.currentTarget.style.background='var(--orange-50)'; }}}
                    onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--line)'; e.currentTarget.style.background='var(--surface, #fff)'; }}
                  >
                    <span style={{width:30, height:30, flex:'0 0 auto', display:'grid', placeItems:'center', fontSize:16, borderRadius:8, background:'var(--orange-50)', color:'var(--orange-600)'}}>
                      {busy ? '…' : d.icon}
                    </span>
                    <span style={{minWidth:0}}>
                      <span style={{display:'block', fontSize:12.5, fontWeight:700, color:'var(--ink-1)'}}>{d.label}</span>
                      <span style={{display:'block', fontSize:10.5, color:'var(--ink-3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{busy ? '로그인 중…' : d.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p style={{fontSize:10.5, color:'var(--ink-3)', marginTop:10, textAlign:'center'}}>
              {activeRegion ? `${activeRegion.name} · ` : ''}선택 즉시 해당 유형의 데모 계정으로 로그인됩니다.
            </p>
          </div>

          <div className="auth-foot">
            <div className="auth-foot-row">
              <span className="ck">{Ic.check}</span>
              <span>역할(케어매니저·유관기관·보호자·관리자)에 맞는 화면으로 자동 이동합니다.</span>
            </div>
            <div className="auth-foot-row" style={{marginTop:10}}>
              <span className="ck">{Ic.check}</span>
              <span>모든 접근 기록은 5년간 감사 로그로 안전하게 보존됩니다.</span>
            </div>
          </div>
        </div>
      </form>
    </div>
    <AppFooter/>
    </div>
  );
}
export { Auth };
